import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import {
  FseTechSupportCase,
  UseGetFseTechSupportCasesParams,
  FSECase,
  CreateTechSupportCaseRequest,
  CreateTechSupportCaseResponse,
} from './dto.fse';

/*
|--------------------------------------------------------------------------
| GET FSE TECH SUPPORT CASES
|--------------------------------------------------------------------------
*/

export const useGetFSE = ({
  page = 0,
  size = 10,
  searchString,
  sort,
  enabled = true,
}: UseGetFseTechSupportCasesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FSE_TECH_SUPPORT_CASES, page, size, searchString, sort],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortDirection: 'DESC',
      });

      const body: { searchString?: string; sort?: string } = {};
      if (searchString) body.searchString = searchString;
      if (sort) body.sort = sort;

      const response = await LuppApi.post<
        { searchString?: string; sort?: string },
        {
          data: FseTechSupportCase[];
          totalCount: number;
          pageNumber: number;
          pageSize: number;
          currentPageCount: number;
          totalPages: number;
        }
      >(`service/v1/tech-support/searches?${queryParams.toString()}`, body, {
        extractData: (response) => response, // Return full response, not just response.data
      });

      return {
        items: response.data,
        totalCount: response.totalCount,
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        currentPageCount: response.currentPageCount,
        totalPages: response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| GET GSE CASE BY ID
|--------------------------------------------------------------------------
*/

export const useGetFSECaseById = (caseId: string, enabled = true) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FSE_TECH_SUPPORT_CASES, caseId],
    queryFn: async () => {
      const response = await LuppApi.get<FSECase>(`service/v1/tech-support/${caseId}`);
      return response;
    },
    enabled: enabled && !!caseId,
  });
};

/*
|--------------------------------------------------------------------------
| CREATE TECH SUPPORT CASE
|--------------------------------------------------------------------------
*/

export const useCreateTechSupportCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTechSupportCaseRequest) => {
      const response = await LuppApi.post<
        CreateTechSupportCaseRequest,
        CreateTechSupportCaseResponse
      >('service/v1/tech-support', data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FSE_TECH_SUPPORT_CASES] });
    },
  });
};
